import {
	Box,
	Stack,
	Button,
	Text,
	Icons,
	Inline,
	Container,
	Table,
	SelectInput,
	Spinner,
	Tabs,
	TabsList,
	TabItem,
	TabContent,
	Alert,
	TSortOrder,
	SortableHeader,
	Badge,
} from "@sembark-travel/ui-base"
import { useLocationQuery } from "@sembark-travel/ui-router"
import {
	TSearchParams,
	useSearch,
	Search,
	areAdvancedFiltersAppliedDefault,
	ListView,
} from "@sembark-travel/ui-list"
import {
	dateToQuery,
	dateToUTCString,
	parseDateFromQuery,
	startOf,
	endOf,
	subtractUnit,
	addUnit,
	isAfter,
	isBetween,
	utcTimestampToLocalDate,
	utcTimestampToLocalDateTimeString,
} from "@sembark-travel/datetime-utils"
import { IListResponse } from "@sembark-travel/xhr"
import { useEffect, useMemo } from "react"
import { $PropertyType } from "utility-types"
import config from "../config"
import { SelectTripOwners } from "../Trips"
import { IUser } from "../Users"
import { SelectField } from "@sembark-travel/ui-form"
import { TemporaryStorage } from "../DownloadManager"
import {
	LeadStatusType,
	TLeadReportStats,
	useLeadRedirect,
	useLeadReportStats,
} from "./store"
import { IntervalTitle } from "../TripSalesReports"
import { SelectLeadPurposes } from "./List"
import { SelectHotels } from "../Hotels"
import { SelectLeadChannels } from "../LeadChannels"
import { SelectTripSources } from "../TripSources"

export type TTypeWiseReportItem = {
	id: number
	name: string
	email: string
	deleted_at?: string | null
	disabled_at?: string | null
} & TLeadReportStats

type TSortBy =
	| "archived"
	| "moved_to_trips"
	| "pending_followups"
	| "total"
	| "active_followups"

type TFilters = TSearchParams & {
	after: Date
	before: Date
	trip_sources?: Array<{ name: string; id: number }>
	owners?: Array<Pick<IUser, "id" | "name">>
	intervalType: "month" | "week" | "day"
	lead_channels?: Array<{ name: string; id: number }>
	lead_purposes?: Array<{ name: string; id: number }>
	hotels_of_interest?: Array<{ id: number; name: string }>
	type:
		| "user"
		| "source"
		| "lead-channel"
		| "lead-purpose"
		| "hotels-of-interest"
	sort_by?: TSortBy
	sort_order?: TSortOrder
}

type TFiltersInQuery = TSearchParams & {
	after?: string
	before?: string
	trip_sources?: Array<string>
	owners?: Array<string>
	intervalType: "month" | "week" | "day"
	lead_channels?: Array<string>
	lead_purposes?: Array<string>
	hotels_of_interest?: Array<string>
	type?:
		| "user"
		| "source"
		| "lead-channel"
		| "lead-purpose"
		| "hotels-of-interest"
	sort_by?: TSortBy
	sort_order?: TSortOrder
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const {
		q,
		after,
		before,
		page,
		trip_sources,
		owners,
		intervalType,
		type,
		lead_channels,
		lead_purposes,
		hotels_of_interest,
		sort_by,
		sort_order,
	} = filters
	const query: TFiltersInQuery = {
		after: dateToQuery(after || startOf(new Date(), "month")),
		before: dateToQuery(before || endOf(new Date(), "month")),
		intervalType: intervalType || "month",
		type: type || "source",
	}
	if (q) {
		query.q = q
	}
	if (page) query.page = page
	if (trip_sources) {
		query.trip_sources = trip_sources.map((v) => `${v.id}_${v.name}`)
	}
	if (owners) {
		query.owners = owners.map((d) => `${d.id}_${d.name}`)
	}
	if (sort_by) {
		query.sort_by = sort_by
	}
	if (sort_order) {
		query.sort_order = sort_order
	}
	if (lead_channels) {
		query.lead_channels = lead_channels.map((v) => `${v.id}_${v.name}`)
	}
	if (lead_purposes) {
		query.lead_purposes = lead_purposes.map((v) => `${v.id}_${v.name}`)
	}
	if (hotels_of_interest?.length) {
		query.hotels_of_interest = hotels_of_interest.map(
			(h) => `${h.id}_${h.name}`
		)
	}
	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const {
		q,
		page,
		trip_sources,
		owners,
		after,
		before,
		intervalType,
		type,
		lead_channels,
		lead_purposes,
		hotels_of_interest,
		sort_by,
		sort_order,
	} = query
	const filters: TFilters = {
		after: after
			? startOf(parseDateFromQuery(after), "day")
			: startOf(new Date(), intervalType || "month"),
		before: before
			? endOf(parseDateFromQuery(before), "day")
			: endOf(new Date(), intervalType || "month"),
		intervalType: intervalType || "month",
		type: type || "source",
	}
	if (q) {
		filters.q = q
	}
	if (page) filters.page = page

	if (trip_sources?.length) {
		filters.trip_sources = trip_sources.map((v) => {
			const [id, ...name] = v.split("_")
			return {
				id: Number(id),
				name: name.join("_"),
			}
		})
	}
	if (owners) {
		filters.owners = owners.map((d) => {
			const [id, ...name] = d.split("_")
			return {
				id,
				name: name.join("_"),
			}
		}) as unknown as $PropertyType<TFilters, "owners">
	}
	if (lead_channels?.length) {
		filters.lead_channels = lead_channels.map((v) => {
			const [id, ...name] = v.split("_")
			return { name: name.join("_"), id: Number(id) }
		})
	}
	if (lead_purposes?.length) {
		filters.lead_purposes = lead_purposes.map((v) => {
			const [id, ...name] = v.split("_")
			return { name: name.join("_"), id: Number(id) }
		})
	}
	if (hotels_of_interest?.length) {
		filters.hotels_of_interest = hotels_of_interest.map((h) => {
			const [id, ...name] = h.split("_")
			return {
				id: Number(id),
				name: name.join("_"),
			}
		})
	}
	if (sort_by) {
		filters.sort_by = sort_by
	}
	if (sort_order) {
		filters.sort_order = sort_order
	}
	return filters
}

export function LeadReportsList() {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>(query)
	const requestParams = useMemo(() => {
		return {
			q: params.q || null,
			trip_sources: params?.trip_sources?.map((v) => v.id),
			owners: (params.owners || []).map((d) => d.id),
			created_after: dateToUTCString(params.after),
			created_before: dateToUTCString(params.before),
			lead_channels: params?.lead_channels?.map((v) => v.id),
			lead_purposes: params?.lead_purposes?.map((v) => v.id),
			hotels_of_interest: params.hotels_of_interest?.map((h) => h.id),
		}
	}, [params])
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const { stats, error: errorInStats } = useLeadReportStats({
		params: requestParams,
	})
	return (
		<Search
			title="Leads Report"
			initialParams={params}
			onSearch={setParams}
			Filters={Filters}
			resetParams={(params) => ({
				q: "",
				after: params.after,
				before: params.before,
				intervalType: params.intervalType,
				type: params.type,
			})}
			areAdvancedFiltersApplied={(params) => {
				const {
					after,
					before,
					intervalType,
					type,
					sort_by,
					sort_order,
					...others
				} = params
				return areAdvancedFiltersAppliedDefault(others)
			}}
			actions={({ setSearchParams, searchParams }) => (
				<Inline alignItems="center">
					<Button
						onClick={() => {
							const after = startOf(
								subtractUnit(searchParams.after, 1, searchParams.intervalType),
								searchParams.intervalType
							)
							setSearchParams({
								after,
								before: endOf(after, searchParams.intervalType),
							})
						}}
						aria-label="Previous interval"
					>
						<Icons.ChevronDown rotate="90" />
					</Button>
					<Box paddingX="2">
						<SelectInput
							value={searchParams.intervalType}
							onChange={(e) => {
								const type = (e.currentTarget.value ||
									"month") as typeof searchParams.intervalType
								setSearchParams({
									intervalType: type,
									after: startOf(new Date(), type),
									before: endOf(new Date(), type),
								})
							}}
						>
							<option value="month">Month</option>
							<option value="week">Week</option>
							<option value="day">Day</option>
						</SelectInput>
					</Box>
					<Button
						disabled={isAfter(searchParams.before, new Date())}
						onClick={() => {
							const after = startOf(
								addUnit(searchParams.after, 1, searchParams.intervalType),
								searchParams.intervalType
							)
							setSearchParams({
								after,
								before: endOf(after, searchParams.intervalType),
							})
						}}
						aria-label="Next interval"
					>
						<Icons.ChevronDown rotate="270" />
					</Button>
				</Inline>
			)}
		>
			{({ searchParams, setSearchParams }) => (
				<Stack gap="6" paddingY="4">
					<Container>
						<Stack gap="4">
							{errorInStats ? (
								<Alert status="error">
									{errorInStats.message || "Something went wrong."}
								</Alert>
							) : null}
							{stats ? (
								<LeadReportStats
									after={params.after}
									before={params.before}
									intervalType={params.intervalType}
									stats={stats}
									params={params}
								/>
							) : (
								<Box
									paddingY="8"
									bgColor="default"
									width="full"
									textAlign="center"
									rounded="md"
								>
									<Spinner alignCenter />
								</Box>
							)}
						</Stack>
					</Container>
					{stats && stats.total ? (
						<Tabs>
							<TabsList>
								<TabItem
									active={searchParams.type === "source"}
									onClick={() => {
										setSearchParams({
											type: "source",
											sort_by: undefined,
											sort_order: undefined,
										})
									}}
								>
									B2B Sources
								</TabItem>
								<TabItem
									active={searchParams.type === "user"}
									onClick={() => {
										setSearchParams({
											type: "user",
											sort_by: undefined,
											sort_order: undefined,
										})
									}}
								>
									Sales Person
								</TabItem>
								<TabItem
									active={searchParams.type === "lead-channel"}
									onClick={() => {
										setSearchParams({
											type: "lead-channel",
											sort_by: undefined,
											sort_order: undefined,
										})
									}}
								>
									Lead Channel
								</TabItem>
								<TabItem
									active={searchParams.type === "lead-purpose"}
									onClick={() => {
										setSearchParams({
											type: "lead-purpose",
											sort_by: undefined,
											sort_order: undefined,
										})
									}}
								>
									Lead Purpose
								</TabItem>
								<TabItem
									active={searchParams.type === "hotels-of-interest"}
									onClick={() => {
										setSearchParams({
											type: "hotels-of-interest",
											sort_by: undefined,
											sort_order: undefined,
										})
									}}
								>
									Hotel of Interest
								</TabItem>
							</TabsList>

							<TabContent>
								<TypeWiseListView
									type={params.type}
									params={params}
									setParams={setParams}
									requestParams={requestParams}
									onSort={(sortBy, sortOrder) =>
										setSearchParams({
											sort_by: sortBy,
											sort_order: sortOrder,
											page: 1,
										})
									}
								/>
							</TabContent>
						</Tabs>
					) : null}
				</Stack>
			)}
		</Search>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<SelectField
				select={SelectTripSources}
				name="trip_sources"
				label="B2B Sources"
				multiple
				fetchOnMount
			/>
			<SelectField
				select={SelectTripOwners}
				name="owners"
				label="Sales Person"
				multiple
				fetchOnMount
			/>
			<SelectField
				select={SelectLeadChannels}
				name="lead_channels"
				label="Lead Channels"
				multiple
				fetchOnMount
			/>
			<SelectField
				select={SelectLeadPurposes}
				name="lead_purposes"
				label="Lead Purposes"
				multiple
				fetchOnMount
			/>
			<SelectField
				name="hotels_of_interest"
				label="Hotels of Interest"
				select={SelectHotels}
				multiple
				fetchOnMount
				includeDisabled
			/>
		</Stack>
	)
}

type THeadersList = {
	label: string
	sortKey?: TSortBy
}

function TypeWiseListView({
	type,
	params,
	setParams,
	requestParams,
	onSort,
}: {
	type: TFilters["type"]
	params: TFilters
	setParams: (params: TFilters) => void
	requestParams: Record<string, unknown>
	onSort: (sortBy?: TSortBy, sortOrder?: TSortOrder) => void
}) {
	const { onRedirect } =
		useLeadRedirect<Omit<TFiltersInQuery, "intervalType">>()

	const headersList: THeadersList[] = useMemo(
		() => [
			{ label: "Name" },
			{ label: "Total", sortKey: "total" },
			{ label: "Active Follow-Up", sortKey: "active_followups" },
			{ label: "Pending Follow-Up", sortKey: "pending_followups" },
			{ label: "Moved To Trips", sortKey: "moved_to_trips" },
			{ label: "Closed", sortKey: "archived" },
		],
		[]
	)

	const highlightCols = useMemo(() => {
		const result: { [key: number]: boolean } = {}
		headersList.forEach((h, i) => {
			if (h.sortKey && h.sortKey === params.sort_by) {
				result[i] = true
			}
		})
		return result
	}, [params.sort_by, headersList])

	const headers = (isValidating: boolean) =>
		headersList.map((h) =>
			h.sortKey ? (
				<SortableHeader
					key={h.sortKey}
					label={h.label}
					sortKey={h.sortKey}
					currentSortBy={params.sort_by}
					currentSortOrder={params.sort_order}
					isValidating={h.sortKey === params.sort_by ? isValidating : false}
					onSort={(sortBy, sortOrder) => {
						setParams({
							...params,
							sort_by: sortBy,
							sort_order: sortOrder,
							page: 1,
						})
						onSort(sortBy, sortOrder)
					}}
				/>
			) : (
				h.label
			)
		)

	const showDisabledForDeleted = [
		"source",
		"lead-channel",
		"hotels-of-interest",
	].includes(type)

	return (
		<ListView<TTypeWiseReportItem, TFilters>
			pageKey={`leads-reports-${type}`}
			params={params}
			swrConfig={{ keepPreviousData: true }}
			onPageChange={(page) => setParams({ ...params, page })}
			fetch={async (xhr, { trip_sources, ...params }) => {
				const payload = {
					...params,
					trip_sources: trip_sources?.map((v) => v.id) || [],
					owners: (params.owners || []).map((d) => d.id),
					lead_channels: params?.lead_channels?.map((v) => v.id),
					lead_purposes: params?.lead_purposes?.map((v) => v.id),
					hotels_of_interest: params.hotels_of_interest?.map((h) => h.id),
					created_after: dateToUTCString(params.after),
					created_before: dateToUTCString(params.before),
					sort_by: params.sort_by,
					sort_order: params.sort_order,
					show_unassigned: true,
				}

				return await xhr
					.get<
						IListResponse<TTypeWiseReportItem>
					>(`/trip-plan-request-report/stats-by-${type}`, { params: payload })
					.then((data) => data.data)
			}}
			actions={
				<TemporaryStorage
					initUrl={`/trip-plan-request-report/stats-by-${type}/download`}
					initParams={{
						...requestParams,
						timezone_offset: config.timezoneOffset,
					}}
				>
					{({ message, generate, url, reset, isGenerating }) =>
						url ? (
							<Button
								as="a"
								href={url}
								target="_blank"
								download
								level="primary"
								size="sm"
								onClick={() => reset()}
							>
								{message}
							</Button>
						) : (
							<Button
								size="sm"
								onClick={generate}
								disabled={isGenerating}
								level="primary"
							>
								{message || (
									<>
										<Icons.DocumentDownload /> Full Report
									</>
								)}
							</Button>
						)
					}
				</TemporaryStorage>
			}
		>
			{({ items, isValidating }) => {
				return (
					<Table
						bordered
						hover
						responsive
						headers={headers(isValidating)}
						highlightCols={highlightCols}
						rowProps={(rowIndex) => {
							const item = items[rowIndex]
							if (item?.id === null) {
								return {
									style: { backgroundColor: "#f0f0f3" },
								}
							}
							return {}
						}}
						alignCols={{
							1: "right",
							2: "right",
							3: "right",
							4: "right",
							5: "right",
						}}
						rows={items.map(
							({
								name,
								moved_to_trips,
								archived,
								active_followups,
								pending_followups,
								id,
								total,
								deleted_at,
								disabled_at,
							}) => [
								<Stack>
									<Box
										cursor={id === null ? "disabled" : "pointer"}
										onClick={() => {
											if (id === null) return
											const query = {
												ca: dateToQuery(params.after),
												cb: dateToQuery(params.before),
												source: "all",
												trip_sources:
													type === "source"
														? [`${id}_${name}`]
														: params?.trip_sources?.map(
																(v) => `${v.id}_${v.name}`
															),
												lead_channels:
													type === "lead-channel"
														? [`${id}_${name}`]
														: params?.lead_channels?.map(
																(v) => `${v.id}_${v.name}`
															),
												lead_purposes:
													type === "lead-purpose"
														? [`${id}_${name}`]
														: params?.lead_purposes?.map(
																(v) => `${v.id}_${v.name}`
															),
												hotels_of_interest:
													type === "hotels-of-interest"
														? [`${id}_${name}`]
														: params.hotels_of_interest?.map(
																(v) => `${v.id}_${v.name}`
															),
												owners:
													type === "user"
														? [`${id}-${name}`]
														: params?.owners?.map(
																(user) => `${user.id}-${user.name}`
															),
												leadStatus: LeadStatusType.map(
													(type) => `${type.id}-${type.name}`
												),
											}
											onRedirect(query)
										}}
									>
										<Text>{name}</Text>
									</Box>
									{deleted_at &&
									isBetween(
										utcTimestampToLocalDate(deleted_at),
										params.after,
										params.before
									) ? (
										<Box>
											<Badge
												status={showDisabledForDeleted ? "warning" : "danger"}
												title={utcTimestampToLocalDateTimeString(deleted_at)}
											>
												{showDisabledForDeleted ? "Disabled" : "Deleted"}
											</Badge>
										</Box>
									) : disabled_at &&
									  isBetween(
											utcTimestampToLocalDate(disabled_at),
											params.after,
											params.before
									  ) ? (
										<Box>
											<Badge
												warning
												title={utcTimestampToLocalDateTimeString(disabled_at)}
											>
												Disabled
											</Badge>
										</Box>
									) : null}
								</Stack>,

								<Button
									disabled={id === null}
									inline
									onClick={() => {
										if (id === null) return
										const query = {
											ca: dateToQuery(params.after),
											cb: dateToQuery(params.before),
											source: "all",
											trip_sources:
												type === "source"
													? [`${id}_${name}`]
													: params?.trip_sources?.map(
															(v) => `${v.id}_${v.name}`
														),
											lead_channels:
												type === "lead-channel"
													? [`${id}_${name}`]
													: params?.lead_channels?.map(
															(v) => `${v.id}_${v.name}`
														),
											lead_purposes:
												type === "lead-purpose"
													? [`${id}_${name}`]
													: params?.lead_purposes?.map(
															(v) => `${v.id}_${v.name}`
														),
											hotels_of_interest:
												type === "hotels-of-interest"
													? [`${id}_${name}`]
													: params.hotels_of_interest?.map(
															(v) => `${v.id}_${v.name}`
														),
											owners:
												type === "user"
													? [`${id}-${name}`]
													: params?.owners?.map(
															(user) => `${user.id}-${user.name}`
														),
											leadStatus: LeadStatusType.map(
												(type) => `${type.id}-${type.name}`
											),
										}
										onRedirect(query)
									}}
								>
									<Text>{total}</Text>
								</Button>,
								<Text>{active_followups}</Text>,
								<Text>{pending_followups}</Text>,
								<Text>{moved_to_trips}</Text>,
								<Text>{archived}</Text>,
							]
						)}
					/>
				)
			}}
		</ListView>
	)
}

function LeadReportStats({
	stats,
	after,
	before,
	intervalType,
	params,
}: {
	stats: TLeadReportStats
	params: TFilters
} & Pick<TFilters, "after" | "before" | "intervalType">) {
	const {
		archived,
		moved_to_trips,
		pending_followups,
		total,
		active_followups,
		ai_generated = 0,
	} = stats
	const { onRedirect } =
		useLeadRedirect<Omit<TFiltersInQuery, "intervalType">>()
	return (
		<Inline
			bgColor="default"
			roundedRight="lg"
			borderLeftWidth="4"
			borderColor="accent"
			paddingY="2"
			justifyContent="between"
			alignItems="stretch"
			collapseBelow="md"
			gap="4"
		>
			<Inline paddingLeft={"2"} gap="2" alignItems="center" collapseBelow="sm">
				<Box
					paddingX={{ xs: "4", sm: "8" }}
					paddingY="4"
					borderRightWidth={{ sm: "4" }}
					borderBottomWidth={{ xs: "4", sm: "0" }}
					borderColor="default"
				>
					<IntervalTitle
						after={after}
						before={before}
						intervalType={intervalType}
					/>
				</Box>

				<Inline
					gap={{ xs: "4", sm: "8" }}
					paddingY="4"
					paddingX={{ xs: "4", sm: "8" }}
					flexWrap={"wrap"}
					justifyContent={"center"}
				>
					<Stack
						cursor="pointer"
						onClick={() => {
							const query = {
								ca: dateToQuery(params.after),
								cb: dateToQuery(params.before),
								source: "all",
								trip_sources: params?.trip_sources?.map(
									(v) => `${v.id}_${v.name}`
								),
								lead_channels: params?.lead_channels?.map(
									(v) => `${v.id}_${v.name}`
								),
								lead_purposes: params?.lead_purposes?.map(
									(v) => `${v.id}_${v.name}`
								),
								hotels_of_interest: params.hotels_of_interest?.map(
									(v) => `${v.id}_${v.name}`
								),
								owners: params?.owners?.map(
									(user) => `${user.id}-${user.name}`
								),
								leadStatus: LeadStatusType.map(
									(type) => `${type.id}-${type.name}`
								),
							}
							onRedirect(query)
						}}
						paddingX={{ xs: "4", sm: "8" }}
					>
						<Text fontWeight="semibold" color="muted">
							Total Lead
						</Text>
						<Box textAlign={"center"} fontWeight="semibold" fontSize="3xl">
							<Text>{total}</Text>
						</Box>
					</Stack>
					{ai_generated > 0 ? (
						<Stack
							cursor="pointer"
							onClick={() => {
								const query = {
									ca: dateToQuery(params.after),
									cb: dateToQuery(params.before),
									source: "all",
									trip_sources: params?.trip_sources?.map(
										(v) => `${v.id}_${v.name}`
									),
									lead_channels: params?.lead_channels?.map(
										(v) => `${v.id}_${v.name}`
									),
									lead_purposes: params?.lead_purposes?.map(
										(v) => `${v.id}_${v.name}`
									),
									hotels_of_interest: params.hotels_of_interest?.map(
										(v) => `${v.id}_${v.name}`
									),
									owners: params?.owners?.map(
										(user) => `${user.id}-${user.name}`
									),
									leadStatus: LeadStatusType.map(
										(type) => `${type.id}-${type.name}`
									),
									ai_only: 1,
								}
								onRedirect(query)
							}}
							paddingX={{ xs: "4", sm: "8" }}
						>
							<Text fontWeight="semibold" color="muted">
								<Icons.SparklesSolid /> AI Leads
							</Text>
							<Box textAlign={"center"} fontWeight="semibold" fontSize="3xl">
								<Text>{ai_generated}</Text>
							</Box>
						</Stack>
					) : null}
					<Stack
						cursor="pointer"
						onClick={() => {
							const query = {
								ca: dateToQuery(params.after),
								cb: dateToQuery(params.before),
								source: "all",
								trip_sources: params?.trip_sources?.map(
									(v) => `${v.id}_${v.name}`
								),
								lead_channels: params?.lead_channels?.map(
									(v) => `${v.id}_${v.name}`
								),
								lead_purposes: params?.lead_purposes?.map(
									(v) => `${v.id}_${v.name}`
								),
								hotels_of_interest: params.hotels_of_interest?.map(
									(v) => `${v.id}_${v.name}`
								),
								owners: params?.owners?.map(
									(user) => `${user.id}-${user.name}`
								),
								leadStatus: ["active_followups-Active Followup"],
							}
							onRedirect(query)
						}}
					>
						<Text fontWeight="semibold" color="muted">
							Active Follow-Up
						</Text>
						<Text textAlign={"center"} fontWeight="semibold" fontSize="3xl">
							{active_followups}
						</Text>
					</Stack>
					<Stack
						cursor="pointer"
						onClick={() => {
							const query = {
								ca: dateToQuery(params.after),
								cb: dateToQuery(params.before),
								source: "all",
								trip_sources: params?.trip_sources?.map(
									(v) => `${v.id}_${v.name}`
								),
								lead_channels: params?.lead_channels?.map(
									(v) => `${v.id}_${v.name}`
								),
								lead_purposes: params?.lead_purposes?.map(
									(v) => `${v.id}_${v.name}`
								),
								hotels_of_interest: params.hotels_of_interest?.map(
									(v) => `${v.id}_${v.name}`
								),
								owners: params?.owners?.map(
									(user) => `${user.id}-${user.name}`
								),
								leadStatus: ["pending_followups-Pending Followup"],
							}
							onRedirect(query)
						}}
					>
						<Text fontWeight="semibold" color="muted">
							Pending Follow-Up
						</Text>
						<Text textAlign={"center"} fontWeight="semibold" fontSize="3xl">
							{pending_followups}
						</Text>
					</Stack>
					<Stack
						cursor="pointer"
						onClick={() => {
							const query = {
								ca: dateToQuery(params.after),
								cb: dateToQuery(params.before),
								source: "all",
								trip_sources: params?.trip_sources?.map(
									(v) => `${v.id}_${v.name}`
								),
								lead_channels: params?.lead_channels?.map(
									(v) => `${v.id}_${v.name}`
								),
								lead_purposes: params?.lead_purposes?.map(
									(v) => `${v.id}_${v.name}`
								),
								hotels_of_interest: params.hotels_of_interest?.map(
									(v) => `${v.id}_${v.name}`
								),
								owners: params?.owners?.map(
									(user) => `${user.id}-${user.name}`
								),
								leadStatus: ["moved-Moved"],
							}
							onRedirect(query)
						}}
					>
						<Text fontWeight="semibold" color="muted">
							Moved To Trips
						</Text>
						<Text textAlign={"center"} fontWeight="semibold" fontSize="3xl">
							{moved_to_trips}
						</Text>
					</Stack>
					<Stack
						cursor="pointer"
						onClick={() => {
							const query = {
								ca: dateToQuery(params.after),
								cb: dateToQuery(params.before),
								source: "all",
								trip_sources: params?.trip_sources?.map(
									(v) => `${v.id}_${v.name}`
								),
								lead_channels: params?.lead_channels?.map(
									(v) => `${v.id}_${v.name}`
								),
								lead_purposes: params?.lead_purposes?.map(
									(v) => `${v.id}_${v.name}`
								),
								hotels_of_interest: params.hotels_of_interest?.map(
									(v) => `${v.id}_${v.name}`
								),
								owners: params?.owners?.map(
									(user) => `${user.id}-${user.name}`
								),
								leadStatus: ["archived-Archived"],
							}
							onRedirect(query)
						}}
					>
						<Text fontWeight="semibold" color="muted">
							Closed
						</Text>
						<Text textAlign={"center"} fontWeight="semibold" fontSize="3xl">
							{archived}
						</Text>
					</Stack>
				</Inline>
			</Inline>
		</Inline>
	)
}
